// Shared Monaco JSON editor component
// Handles Monaco loading, editor creation, schema validation, and error display

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import { loadMonaco } from '../../lib/monaco-loader.js';

// Events: value-changed (detail: {value: string}), errors-changed (detail: {hasErrors: boolean})
class MonacoJsonEditor extends BaseComponent {
  static properties = {
    value: { type: String },
    schemaUrl: { type: String, attribute: 'schema-url' },
    refSchemaUrl: { type: String, attribute: 'ref-schema-url' },
    loading: { type: Boolean },
    _monacoLoaded: { type: Boolean, state: true },
    _editorErrors: { type: Array, state: true },
  };

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      min-height: 0;
      overflow: hidden;
    }

    .editor-container {
      position: relative;
      flex: 1;
      min-height: 0;
      overflow: hidden;
    }
  `;

  constructor() {
    super();
    this.value = '';
    /** @type {string|null} */
    this.schemaUrl = null;
    /** @type {string|null} */
    this.refSchemaUrl = null;
    this.loading = false;
    this._monacoLoaded = false;
    /** @type {Array<{line: number, message: string}>} */
    this._editorErrors = [];
    /** @type {import('../../types/jsdoc-types.js').MonacoEditor|null} */
    this._editor = null;
    /** @type {import('../../types/jsdoc-types.js').MonacoModule|null} */
    this._monaco = null;
    /** @type {{dispose: () => void}|null} */
    this._markerListenerDisposable = null;
  }

  get hasErrors() {
    return this._editorErrors.length > 0;
  }

  async connectedCallback() {
    await super.connectedCallback();
    if (!this.loading) {
      this._loadMonaco();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._markerListenerDisposable) {
      this._markerListenerDisposable.dispose();
    }
    if (this._editor) {
      this._editor.dispose();
    }
  }

  /** @param {Map<string, unknown>} changedProps */
  updated(changedProps) {
    if (changedProps.has('loading') && !this.loading && !this._monacoLoaded) {
      this._loadMonaco();
    }
    // Sync the editor content when the parent pushes a new value (e.g., version switch)
    if (changedProps.has('value') && this._editor) {
      const currentValue = this._editor.getValue();
      if (this.value !== currentValue) {
        this._editor.setValue(this.value);
      }
    }
  }

  async _loadMonaco() {
    if (this._monacoLoaded) return;

    try {
      const { monaco, styleSheets } = await loadMonaco();
      this._monaco = monaco;
      this._monacoLoaded = true;
      if (this.shadowRoot) {
        this.shadowRoot.adoptedStyleSheets = [...this.shadowRoot.adoptedStyleSheets, ...styleSheets];
      }
      await this.updateComplete;
      this._createEditor();
    } catch (err) {
      console.error('Failed to load Monaco editor:', err);
    }
  }

  _createEditor() {
    const container = /** @type {HTMLElement|null} */ (this.shadowRoot?.querySelector('#monaco-container'));
    if (!container || !this._monaco) return;

    /** @type {import('../../types/jsdoc-types.js').MonacoEditor} */
    // @ts-expect-error - Monaco loaded dynamically from CDN
    const editor = this._monaco.editor.create(container, {
      value: this.value,
      language: 'json',
      theme: 'vs-light',
      minimap: { enabled: false },
      automaticLayout: true,
      formatOnPaste: true,
      formatOnType: true,
      scrollBeyondLastLine: false,
      wordWrap: 'on',
      tabSize: 2,
      fontSize: 14,
    });
    this._editor = editor;

    editor.onDidChangeModelContent(() => {
      this.dispatchEvent(
        new CustomEvent('value-changed', {
          detail: { value: editor.getValue() },
          bubbles: true,
          composed: true,
        }),
      );
    });

    if (this.schemaUrl) {
      this._loadSchema();
    }

    const model = editor.getModel();
    if (model) {
      const updateMarkersState = () => {
        /** @type {Array<{severity: number, message: string, startLineNumber: number}>} */
        // @ts-expect-error - Monaco loaded dynamically from CDN
        const markers = this._monaco.editor.getModelMarkers({ owner: 'json' });
        const problemMarkers = markers.filter(
          (/** @type {{severity: number}} */ m) =>
            // @ts-expect-error - Monaco loaded dynamically from CDN
            m.severity === this._monaco.MarkerSeverity.Error ||
            // @ts-expect-error - Monaco loaded dynamically from CDN
            m.severity === this._monaco.MarkerSeverity.Warning,
        );
        this._editorErrors = problemMarkers.map((/** @type {{startLineNumber: number, message: string}} */ m) => ({
          line: m.startLineNumber,
          message: m.message,
        }));
        this.dispatchEvent(
          new CustomEvent('errors-changed', {
            detail: { hasErrors: this._editorErrors.length > 0 },
            bubbles: true,
            composed: true,
          }),
        );
      };

      // @ts-expect-error - Monaco loaded dynamically from CDN
      this._markerListenerDisposable = this._monaco.editor.onDidChangeMarkers(() => {
        updateMarkersState();
      });

      setTimeout(updateMarkersState, 100);
    }
  }

  async _loadSchema() {
    if (!this._monaco || !this.schemaUrl) return;

    try {
      const fetches = [fetch(this.schemaUrl)];
      if (this.refSchemaUrl) {
        fetches.push(fetch(this.refSchemaUrl));
      }

      const responses = await Promise.all(fetches);
      const primaryResponse = responses[0];
      if (!primaryResponse) return;

      if (!primaryResponse.ok) {
        console.warn(`Failed to load schema: ${this.schemaUrl}`);
        return;
      }

      const primarySchema = await primaryResponse.json();
      const schemas = [
        {
          uri: `${window.location.origin}${this.schemaUrl}`,
          fileMatch: ['*'],
          schema: primarySchema,
        },
      ];

      if (this.refSchemaUrl && responses[1]?.ok) {
        const refSchema = await responses[1].json();
        schemas.push({
          uri: `${window.location.origin}${this.refSchemaUrl}`,
          fileMatch: [],
          schema: refSchema,
        });
      }

      // @ts-expect-error - Monaco loaded dynamically from CDN
      this._monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: true,
        enableSchemaRequest: true,
        schemas: schemas,
      });
    } catch (err) {
      console.warn('Error loading schema for Monaco:', err);
    }
  }

  render() {
    if (this.loading || !this._monacoLoaded) {
      return html`
        <div class="text-center py-4">
          <div class="spinner-border">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      `;
    }

    return html`
      ${
        this._editorErrors.length > 0
          ? html`
            <div class="alert alert-danger py-2 mb-2">
              <ul class="mb-0 ps-3">
                ${this._editorErrors.map((err) => html` <li>Line ${err.line}: ${err.message}</li> `)}
              </ul>
            </div>
          `
          : ''
      }
      <div class="border rounded editor-container">
        <div id="monaco-container" style="width: 100%; height: 100%;"></div>
      </div>
    `;
  }
}

customElements.define('monaco-json-editor', MonacoJsonEditor);
